import { useState } from 'react';
import {
  Box,
  Paper,
  Title,
  Text,
  TextInput,
  Button,
  Stack,
  Alert,
  Flex,
  Image,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { AlertCircle, Mail, Check } from 'react-feather';
import { authService } from '../../services/api/auth';
import classes from './LoginPage.module.css';
import medleyLogoWhite from '../../assets/medley-logo-white.png';

type Persona = 'coach' | 'partner' | 'participant';

interface MagicLinkLoginPageProps {
  persona: Persona;
}

interface MagicLinkFormValues {
  email: string;
}

const PERSONA_LABELS: Record<Persona, string> = {
  coach: 'Coach Login',
  partner: 'Partner Login',
  participant: 'Participant Login',
};

export function MagicLinkLoginPage({ persona }: MagicLinkLoginPageProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const title = PERSONA_LABELS[persona];

  const form = useForm<MagicLinkFormValues>({
    initialValues: {
      email: '',
    },
    validate: {
      email: value =>
        /^\S+@\S+$/.test(value.trim()) ? null : 'Invalid email address',
    },
  });

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').trim();
    form.setFieldValue('email', pastedText);
  };

  const handleSubmit = async (values: MagicLinkFormValues) => {
    setSubmitError(null);
    setIsSubmitting(true);
    const trimmedEmail = values.email.trim();

    try {
      await authService.requestMagicLink(trimmedEmail);
      setIsSuccess(true);
    } catch (err) {
      // MED-843: distinguish rate-limit (429) and server errors (5xx) from the
      // generic "Check your email" success state. 4xx-non-429 (and network
      // failures) still resolve to success to preserve email-enumeration
      // protection.
      const e = err as { status?: number; message?: string };
      if (e.status && e.status >= 500) {
        setSubmitError('Something went wrong. Please try again.');
      } else if (e.status === 429 || e.message?.includes('Too many requests')) {
        setSubmitError(
          "You've requested several links recently. Please check your inbox or try again in a few minutes.",
        );
      } else {
        setIsSuccess(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <Box className={classes.wrapper}>
        <div className={classes.pattern} />
        <Flex className={classes.container} justify="center">
          <Paper className={classes.formCard} p={40} radius="lg" shadow="xl" maw={420}>
            <Stack align="center" gap="md">
              <Box
                p="md"
                style={{
                  backgroundColor: 'rgba(45, 55, 72, 0.1)',
                  borderRadius: '50%',
                }}
              >
                <Check size={32} color="#2D3748" />
              </Box>
              <Title order={3} ta="center">
                Check your email
              </Title>
              <Text c="dimmed" ta="center">
                If an account exists with that email address, we've sent a magic
                link to log you in.
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                The link expires in 15 minutes. Check your spam folder if you
                don't see it.
              </Text>
              <Button
                variant="light"
                color="dark"
                onClick={() => {
                  setIsSuccess(false);
                  form.reset();
                }}
                fullWidth
                mt="md"
              >
                Try another email
              </Button>
            </Stack>
          </Paper>
        </Flex>
      </Box>
    );
  }

  return (
    <Box className={classes.wrapper}>
      <div className={classes.pattern} />

      <Flex className={classes.container}>
        {/* Left Side - Branding */}
        <Box className={classes.branding}>
          <Box
            mb={48}
            p="sm"
            style={{
              backgroundColor: '#2D4739',
              borderRadius: 8,
              display: 'inline-block',
            }}
          >
            <Image src={medleyLogoWhite} alt="Medley" h={28} w="auto" fit="contain" />
          </Box>

          <Title order={1} className={classes.headline}>
            Welcome to
            <br />
            Medley SGOS
          </Title>

          <Text size="lg" c="dimmed" mt="lg" maw={400}>
            Small Groups OS is a coaching platform designed to support
            professional growth and development.
          </Text>
        </Box>

        {/* Right Side - Form */}
        <Box className={classes.formSection}>
          <Paper className={classes.formCard} p={40} radius="lg" shadow="xl">
            <Title order={2} mb={4}>
              {title}
            </Title>

            <Text c="dimmed" size="sm" mb="xl">
              Enter your email to receive a magic link
            </Text>

            {submitError && (
              <Alert
                icon={<AlertCircle size={16} />}
                title="Error"
                color="red"
                mb="md"
                onClose={() => setSubmitError(null)}
                withCloseButton
              >
                {submitError}
              </Alert>
            )}

            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                <TextInput
                  label="Email address"
                  placeholder="you@company.com"
                  leftSection={<Mail size={18} />}
                  size="md"
                  {...form.getInputProps('email')}
                  onPaste={handlePaste}
                />

                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  mt="sm"
                  loading={isSubmitting}
                >
                  Send Magic Link
                </Button>
              </Stack>
            </form>
          </Paper>
        </Box>
      </Flex>
    </Box>
  );
}

export default MagicLinkLoginPage;
